import { useWatch } from 'react-hook-form';
import { DateRangeInput } from 'v5/design-system/DateRangeInput';
import { FormField } from 'v5/design-system/Form';
import type { DateRangeValue } from 'v5/core/entity-values/values.types';
import { dateConvert } from 'v5/platform/lang/dateConvert';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldFrameSpec, FieldPartProps } from './fieldComponent.types';

const EMPTY_RANGE: DateRangeValue = { from: null, to: null };

/**
 * Date range over `{ from: Date | null; to: Date | null }`; either end may be
 * missing. The form holds `Date`s (the codec's value); the input takes
 * `CalendarDate`s.
 */
export function DateRangeField({
  spec,
  path,
  hideLabel
}: FieldPartProps<FieldFrameSpec>) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const range =
    useWatch<Record<string, DateRangeValue | undefined>>({ name: path }) ??
    EMPTY_RANGE;

  if (readOnly && range.from === null && range.to === null) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field }) => {
        const value: DateRangeValue = field.value ?? EMPTY_RANGE;
        return (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            <DateRangeInput
              className="w-full"
              variant={ctx.variant}
              size={ctx.size}
              readOnly={readOnly}
              aria-label={spec.label}
              value={{
                from: dateConvert.calendarDate.from(value.from),
                to: dateConvert.calendarDate.from(value.to)
              }}
              onChange={next =>
                field.onChange({
                  from: dateConvert.calendarDate.toLocalDate(
                    next?.from ?? null
                  ),
                  to: dateConvert.calendarDate.toLocalDate(next?.to ?? null)
                })
              }
            />
          </FieldControlBase>
        );
      }}
    />
  );
}
